import React, { useMemo, useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import snakeCase from "lodash/snakeCase";

import { ProjectContext } from "../../../contexts/project";
import { EMPTY_STRING_SEARCH_TOKEN } from "../../../constants/search";
import { unitOfMeasureOptions } from "../../../constants/fields/unitOfMeasure";
import useFilteredBillingTypes, {
  getProjectStorageBillingTypeOptions,
} from "../../../hooks/useFilteredBillingTypes";

function BottomAnchoredPopper(popperProps) {
  return (
    <Popper
      {...popperProps}
      placement="bottom-start"
      modifiers={[
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
        { name: "offset", options: { offset: [0, 8] } },
      ]}
    />
  );
}

// Determine field type for special input handling
const getFieldType = fieldValue => {
  if (!fieldValue) return null;
  const snaked = snakeCase(fieldValue);

  const timestampFields = ["created_at", "updated_at", "last_in", "last_out"];
  if (timestampFields.includes(snaked)) {
    return { type: "timestamp", format: "MM/DD/YYYY or MM/YYYY" };
  }

  const dateFormatMap = {
    expiration_month_day_year: { type: "date", format: "MM/DD/YYYY", maxLength: 10 },
    manufacture_date: { type: "date", format: "MM/DD/YYYY", maxLength: 10 },
    expiration_date: { type: "date", format: "MM/YYYY", maxLength: 7 },
    expiration_month: { type: "month", format: "MM", maxLength: 2 },
    expiration_year: { type: "year", format: "YYYY", maxLength: 4 },
  };

  return dateFormatMap[snaked] || null;
};

// Format date input value as user types
const formatDateInput = (value, fieldType) => {
  let formatted = value.replace(/[^\d/]/g, "");

  if (fieldType.type === "month") {
    return formatted.slice(0, 2);
  }
  if (fieldType.type === "year") {
    return formatted.slice(0, 4);
  }
  if (fieldType.format === "MM/YYYY") {
    if (formatted.length >= 2 && formatted.charAt(2) !== "/") {
      formatted = formatted.slice(0, 2) + "/" + formatted.slice(2);
    }
    return formatted.slice(0, 7);
  }
  // MM/DD/YYYY or MM/DD/YYYY or MM/YYYY
  if (formatted.length >= 2 && formatted.charAt(2) !== "/") {
    formatted = formatted.slice(0, 2) + "/" + formatted.slice(2);
  }
  if (formatted.length >= 5 && formatted.charAt(5) !== "/") {
    formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
  }
  return formatted.slice(0, 10);
};

export default function Step1({
  fields,
  fieldValue,
  findText,
  searchOperator,
  onFieldChange,
  onFindTextChange,
  onSearchOperatorChange,
  onBack,
  onNext,
}) {
  const project = useContext(ProjectContext);
  const filteredBillingTypeOptions = useFilteredBillingTypes(
    getProjectStorageBillingTypeOptions(project?.billingTypes)
  );
  const billingTypeOptions = useMemo(
    () => filteredBillingTypeOptions.map(([id, name]) => ({ id, name })),
    [filteredBillingTypeOptions]
  );

  const filteredFields = (fields || [])
    .filter(
      f =>
        f.visible &&
        !f.inactive &&
        f.type !== "checkbox" &&
        f.type !== "photo" &&
        !f.disableFillDown
    )
    .filter(
      f =>
        ![
          "itemId",
          "photoCarousel",
          "rackLocations",
          "BULK_CHECKBOX",
          "photoUpload",
          "warehousesFromRackLocations",
          "warehouseQuantity",
          "allocatedQuantity",
          "reservedQuantity",
          "inCartQuantity",
          "daysLeftToExpire",
        ].includes(f.id)
    );

  const safeFieldName = field => {
    const value = field?.label ?? field?.id ?? "";
    return typeof value === "string" ? value : String(value);
  };

  const fieldSuggestions = filteredFields
    .map(f => ({ id: f.id, name: safeFieldName(f) }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const fieldType = useMemo(() => getFieldType(fieldValue), [fieldValue]);

  const predefinedOptions = useMemo(() => {
    if (!fieldValue || !project) return null;
    const snaked = snakeCase(fieldValue);

    const fieldOptionsMap = {
      id_cost_center: project.costCenters
        ?.filter(cc => cc.costCenterName)
        .map(cc => ({
          id: cc.costCenterName,
          name: cc.costCenterName,
        })),
      pallet_type: project.palletTypes
        ?.filter(pt => pt.isActive && pt.id)
        .map(pt => ({
          id: pt.id,
          name: pt.name,
        })),
      billing_type: billingTypeOptions,
      unit_of_measure: unitOfMeasureOptions.map(o => ({
        id: o.value,
        name: o.label,
      })),
    };

    return fieldOptionsMap[snaked] || null;
  }, [billingTypeOptions, fieldValue, project]);

  // Clear find text when field changes and input type differs
  const handleFieldChange = newFieldId => {
    onFieldChange(newFieldId);
    onFindTextChange("");
  };

  const handleDateInputChange = e => {
    const formatted = formatDateInput(e.target.value, fieldType);
    onFindTextChange(formatted);
  };

  const isEmptySearchSelected = findText === EMPTY_STRING_SEARCH_TOKEN;

  const handleEmptySearchClick = () => {
    onFindTextChange(isEmptySearchSelected ? "" : EMPTY_STRING_SEARCH_TOKEN);
  };

  const isValid = fieldValue && findText;

  const emptyChip = fieldValue && (!findText || isEmptySearchSelected) ? (
    <Chip
      label="Empty"
      size="small"
      clickable
      color={isEmptySearchSelected ? "primary" : "default"}
      variant={isEmptySearchSelected ? "filled" : "outlined"}
      onMouseDown={handleEmptySearchClick}
      sx={{ cursor: "pointer" }}
    />
  ) : null;

  const renderFindInput = () => {
    if (predefinedOptions) {
      return isEmptySearchSelected ? (
        <TextField
          fullWidth
          label="Find"
          value=""
          placeholder="Empty"
          disabled
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{emptyChip}</InputAdornment>
            ),
          }}
        />
      ) : (
        <Autocomplete
          fullWidth
          PopperComponent={BottomAnchoredPopper}
          options={predefinedOptions}
          getOptionLabel={option => option.name || ""}
          value={predefinedOptions.find(o => o.id === findText) || null}
          onChange={(event, newValue) => onFindTextChange(newValue ? newValue.id : "")}
          isOptionEqualToValue={(option, val) => option.id === val?.id}
          ListboxProps={{ style: { maxHeight: "300px", overflow: "auto" } }}
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) return options;
            const term = inputValue.toLowerCase();
            return options.filter(o => (o?.name || "").toLowerCase().includes(term));
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Find"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment position="end">{emptyChip}</InputAdornment>
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    if (fieldType) {
      return (
        <TextField
          fullWidth
          label="Find"
          placeholder={fieldType.format}
          value={findText}
          onChange={handleDateInputChange}
          disabled={!fieldValue}
          size="small"
          inputProps={{ maxLength: fieldType.maxLength }}
        />
      );
    }

    return (
      <TextField
        fullWidth
        label="Find"
        value={isEmptySearchSelected ? "" : findText}
        placeholder={isEmptySearchSelected ? "Empty" : undefined}
        onChange={e => onFindTextChange(e.target.value)}
        disabled={!fieldValue || isEmptySearchSelected}
        size="small"
        InputProps={{
          endAdornment: emptyChip ? (
            <InputAdornment position="end">{emptyChip}</InputAdornment>
          ) : null,
        }}
      />
    );
  };

  return (
    <>
      <Box mb={3}>
        <Typography fontWeight={500} fontSize={20}>
          Find and Replace
        </Typography>
      </Box>
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              fullWidth
              PopperComponent={BottomAnchoredPopper}
              options={fieldSuggestions}
              getOptionLabel={option => option.name || ""}
              value={fieldSuggestions.find(f => f.id === fieldValue) || null}
              onChange={(event, newValue) => handleFieldChange(newValue ? newValue.id : "")}
              isOptionEqualToValue={(option, val) => option.id === val?.id}
              ListboxProps={{
                style: {
                  maxHeight: "300px",
                  overflow: "auto",
                },
              }}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                const term = inputValue.toLowerCase();
                return options.filter(o => (o?.name || "").toLowerCase().includes(term));
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Search Within"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={12}>{renderFindInput()}</Grid>
        </Grid>
      </Box>
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
        <FormControl variant="standard" size="small">
          <Select
            value={searchOperator}
            onChange={e => onSearchOperatorChange(e.target.value)}
            disableUnderline
            sx={{ "& .MuiSelect-select": { pl: 1.75, pr: 3, py: 0 }, fontSize: 16 }}
          >
            <MenuItem value=":">Exact match</MenuItem>
            <MenuItem value="~">Partial match</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={onNext}
          disabled={!isValid}
          sx={{ minWidth: 140 }}
        >
          Find Matches
        </Button>
      </Box>
    </>
  );
}
